<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        
        <!-- <busy-spinner v-if="loadingList" key="loading" inner-class="my-4"></busy-spinner> -->

        <div class="">
        
            <div class="row">  <!-- Top Section -->
                <div class="col-6">  <!-- Left Side -->
                    <h5 class="mb-1">
                        {{ title }}
                        <b-badge 
                            v-if="titleBadgeText" 
                            :variant="titleBadgeVariant" 
                            :class="titleBadgeClass"
                        >
                            {{ titleBadgeText }}
                        </b-badge>
                        
                    </h5>
                    <p v-if="subtitle" class="text-muted small mb-0">
                        {{ subtitle }}
                    </p>
                </div>
                <div class="col-6">  <!-- Right Side -->
                    <div v-if="showThreadAddress">
                        <div class="text-uppercase font-size-70 text-muted text-right mb-1">
                            <base-popover-icon
                                title="Add by Email"
                                content="You can send an email to this special email address to add a message to this conversation.  You must use the same email address registered to your account."
                                class="mr-1"
                            ></base-popover-icon>
                            Add {{ messageName }} by email
                        </div>
                        <base-clipboard-copy-input
                            size="sm"
                            id-input="thread-inbound-address"
                            button-text-start=""
                            button-text-success=""
                            input-group-class="min-width-250"
                            :copy-text="threadData.inbound_email_address"
                        ></base-clipboard-copy-input>
                    </div>
                    <div 
                        v-if="!hideMembers && membershipData.members && membershipData.members.length"
                        class="animate__animated animate__fadeIn animate__faster text-right"
                        :class="showNotifyMemberSelections ? 'shadow border rounded p-2' : ''"
                    >  <!-- Participants / Members -->
                        <div 
                            v-if="showNotifyMemberSelections"
                            key="notifyEditTrue"
                            class="position-relative text-left"
                        >
                            <b-overlay :show="loadingNotifyEdit">
                                <div class="font-weight-bold mb-1">Add / Remove {{ notifyMemberName }}</div>
                                <div class="text-muted small mb-2">{{ notifyMemberModifyDescription }}</div>
                                <b-form-checkbox-group
                                    v-model="notifyMemberSelections"
                                    :options="notifyMemberOptions"
                                    text-field="full_name"
                                    value-field="id"
                                    size="sm"
                                ></b-form-checkbox-group>
                                <div class="mt-3 text-right">
                                    <b-button variant="light-secondary" size="sm" @click="cancelNotifyEdit">Cancel</b-button>
                                    <b-button variant="sharp-primary" size="sm" class="ml-2" @click="saveNotifyEdit">Save</b-button>
                                </div>
                            </b-overlay>
                        </div>

                        <div v-else key="notifyEditFalse">
                            <div 
                                v-if="membershipData.notify_members_to_show && membershipData.notify_members_to_show.length" 
                                class=""
                            >
                                <div class="mb-1 text-muted small">
                                    <span class="text-uppercase">{{ notifyMemberName }}</span> 
                                    <base-popover-icon
                                        :title="notifyMemberName"
                                        :content="notifyMemberPopoverDescription"
                                        class="ml-2"
                                    ></base-popover-icon>
                                </div>
                                <div class="d-flex flex-wrap justify-content-end">
                                    <div v-for="tu in membershipData.notify_members_to_show" :key="tu.id" class="mr-2 mb-1">
                                        <!-- <span v-if="index !== 0" class="mx-2">&middot;</span>
                                        {{ tu.full_name }} -->
                                        <tenant-user-badge :tu-data="tu"></tenant-user-badge>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="membershipData.current_tu_perms && membershipData.current_tu_perms.can_unsubscribe_others"
                                class="d-inline-block ml-3"
                            >
                                <b-button 
                                    variant="bare-primary"
                                    size="sm"
                                    @click="startNotifyEdit"
                                >Modify {{ notifyMemberName }}</b-button>
                            </div>
                        </div>

                    </div>
                    
                </div>
                
            </div>
            
            <!-- <div class="d-flex justify-content-between">
                <div class="w-45">
                    <instructions-box>
                        <div class="font-size-80">
                            <p>Add your own comments, send messages to team members, and upload attachments.</p>
                            <p>These comments are only visible to internal team members added to this sourcing event.  They are never visible to vendors.</p>
                        </div>
                    </instructions-box>
                </div>
            </div> -->

            <div 
                class="d-flex justify-content-between align-items-center mb-4 mt-3"
            >
                <div>
                    <b-button
                        v-b-tooltip.hover :title="'Refresh ' + messageNameCapitalized + 's'"
                        variant="subtle-secondary"
                        size="sm"
                        class="rounded-pill text-nowrap"
                        @click="refreshMessages"
                    >
                        <b-icon icon="arrow-repeat"></b-icon>
                        <span class="text-muted text-uppercase ml-1">Refresh</span>
                    </b-button>
                </div>
                <div v-if="enableMessageCreate" class="flex-grow-1 message-thread-header-separator mx-2"></div>
                <div v-if="enableMessageCreate">
                    <!-- <div class="p-0 my-4 w-100 bg-gray-200" style="z-index: 9; height: 2px;"></div> -->
                    <b-button 
                        variant="sharp-primary" 
                        class="create-message-button rounded-pill button-text d-flex align-items-center text-nowrap" 
                        @click="showMessageModal"
                    >  <!-- @click="openNewMessage" -->
                        <b-icon icon="plus" variant="success" font-scale="1.5"></b-icon>
                        <div>{{ createVerb }} {{ messageName }}</div>
                    </b-button>
                </div>
                <div class="flex-grow-1 message-thread-header-separator mx-2"></div>
                <div class="pl-2 d-flex align-items-center">
                    <div 
                        v-if="showSelfNoficationToggle && !loadingList && !loadingNotifyChange && !loadingNotifyEdit" 
                        key="showSelfNotificationToggle"
                        class="d-flex align-items-center"
                    >
                        <div class="text-muted text-uppercase small mr-2 text-right">
                            <base-popover-icon
                                title="Email Notifications"
                                content="Control whether you receive email notifications about this topic.  You will always be notified if someone @mentions you, or includes you as a recipient."
                                class="mr-1"
                            ></base-popover-icon>
                            Email Notifications
                        </div>
                        <div>
                            <b-form-checkbox 
                                switch 
                                size="sm" 
                                :disabled="loadingNotifyChange || viewerIsNotMember"
                                :checked="currentTuIsNotify" 
                                @change="changeCurrentTuNotify"
                            >
                                <div class="d-inline-block text-muted" style="min-width: 30px;">
                                    <span v-if="!loadingNotifyChange && !loadingList">{{ currentTuIsNotify ? 'ON' : 'OFF' }}</span>
                                </div>
                            </b-form-checkbox>
                        </div>
                    </div>
                    <div v-else-if="!loadingList && !loadingNotifyChange && !loadingNotifyEdit" key="noSelfNotificationToggle">
                        <div class="text-muted text-uppercase small mr-2 text-right">
                            <base-popover-icon
                                title="No Email Notifications"
                                content="You will not receive email notifications about this topic because you are not subscribed."
                                class="mr-1"
                            ></base-popover-icon>
                            No Email Notifications
                        </div>
                    </div>
                </div>
            </div>

            <div>  <!-- New Message Area -->
                <b-collapse id="new-message-expander" v-model="newMessageExpanderOpen">
                    <b-card class="shadow-dark mb-5" body-class="py-3">
                        <base-message-form
                            v-if="threadId && threadData && threadData.id"
                            :thread-id="threadId"
                            :tenant-users="membershipData.write_members"
                            :notify-tenant-users="membershipData.notify_members_other"
                            :hide-members="hideMembers || hideExternalMembers || hideInternalMembers"
                            :create-verb="createVerb"
                            :comment-name="messageName"
                            :no-files="noFiles"
                            :thread-type="threadType"
                            :editor-style="editorStyle"
                            @created="handleMsgCreated"
                            @cancel="cancelNewMessage"
                        ></base-message-form>
                    </b-card>
                </b-collapse>
            </div>
            
            
            <div>  <!-- List Area -->
                <b-overlay :show="loadingList" rounded="lg" variant="white" blur="5px" class="min-height-150">
                    <template #overlay>
                        <div class="h-100 d-flex justify-content-center align-items-center">
                            <b-spinner></b-spinner>
                        </div>
                    </template>
                    <div v-if="accessDenied" key="accessDenied" class="py-5 text-center">
                        <div class="special-text text-danger">
                            You do not have access to these {{ messageName | plural }}
                        </div>
                        <!-- <div class="mt-3 text-muted">
                            Think that's an error?
                            <br> 
                            <b-button variant="minimal-primary" class="mt-1" size="sm" @click="$helpscout.openAsk()">Contact Supplios Support</b-button>
                        </div> -->
                    </div>
                    <div 
                        v-else-if="!loadingList && !messageList.length" 
                        key="noMessages" 
                        class="py-5 text-center special-text text-muted"
                    >
                        No {{ messageName | plural }} yet
                    </div>
                    <div v-else key="hasMessages">
                        <transition-group 
                            name="message-list" 
                            tag="div"
                            class="message-list-wrapper"
                        >
                            <div 
                                v-for="message in messageList" 
                                :key="message.id"
                                class="message-list-item"
                            >
                                <base-message 
                                    :message="message"
                                    :last-read-on="threadLastReadOnDateString"
                                    :thread-type="threadType"
                                    :disable-reply-button="!enableMessageCreate"
                                    @deleted="refreshMessages"
                                    @edit="handleEditRequest"
                                    @reply="handleReplyRequest"
                                ></base-message>
                            </div>
                        </transition-group>
                    </div>
                </b-overlay>
            </div>
            
        </div>

        <b-modal
            v-model="msgCreateModal"
            centered
            size="md"
            :title="createVerb + ' ' + messageNameCapitalized + ' - ' + title"
            hide-header
            hide-footer
            content-class="bg-gray-50"
            @hidden="msgCreateModalHidden"
        >
            <base-message-form
                v-if="threadId && threadData && threadData.id"
                :thread-id="threadId"
                :tenant-users="membershipData.write_members"
                :notify-tenant-users="membershipData.notify_members_other"
                :hide-members="hideMembers || hideExternalMembers || hideInternalMembers"
                :create-verb="createVerb"
                :comment-name="messageName"
                :no-files="noFiles"
                :thread-type="threadType"
                :editor-style="editorStyle"
                @created="handleMsgCreated"
                @cancel="msgCreateModal = false"
            ></base-message-form>
        </b-modal>

        <b-modal
            v-model="msgEditModal"
            centered
            size="md"
            :title="'Edit ' + messageNameCapitalized + ' - ' + title"
            hide-header
            hide-footer
            content-class="bg-gray-50"
            @hidden="msgEditModalHidden"
        >
            <base-message-form
                v-if="threadId && threadData && threadData.id"
                :thread-id="threadId"
                :tenant-users="membershipData.write_members"
                :notify-tenant-users="membershipData.notify_members_other"
                :hide-members="hideMembers || hideExternalMembers || hideInternalMembers"
                :create-verb="createVerb"
                :edit-comment="editMsg"
                :comment-name="messageName"
                :no-files="noFiles"
                :thread-type="threadType"
                :editor-style="editorStyle"
                @updated="handleMsgUpdated"
                @cancel="msgEditModal = false"
            ></base-message-form>
        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { currentUserInfoMixin, errorHandling } from '../../js/custom-vue-mixins';
import BaseMessage from './BaseMessage';
import BaseMessageForm from './BaseMessageForm';
import BaseClipboardCopyInput from '../BaseClipboardCopyInput';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import has from 'lodash/has';
import { FormatCapitalizeHelper } from '../../js/filters-helpers';
//import without from 'lodash/without';

export default {
    name: 'BaseMessageThread',
    components: {
        BaseMessage,
        BaseMessageForm,
        BaseClipboardCopyInput,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        threadId: {
            type: [String, Number],
        },
        // threadType controls behavior / what to show, etc.
        threadType: {  // options are 'comment' and 'email' -- possibly replace with field on MessageThread model (only if needed on backend)
            type: String,
            default: 'comment',
        },
        // editorStyle just controls what type of editor to show.... you *could* use rich text editor with comments or email, for example
        editorStyle: {  // can be 'rich' or 'textarea' -- 'comment' can use either, 'email' will always use 'rich'
            type: String,
            default: 'textarea'
        },
        title: {
            type: String,
            default: 'Comments',
        },
        subtitle: {
            type: String,
            required: false,
        },
        createVerb: {  // verb for creating new thing -- like 'Add' or 'Send' or something similar
            type: String,
            default: 'Add',
        },
        messageName: {  // singular, non-capitalized name for this message... like 'message' or 'comment' or 'note'
            type: String,
            default: 'comment',
        },
        titleBadgeText: {
            type: String,
            required: false,
        },
        titleBadgeVariant: {
            type: String,
            default: 'soft-primary',
        },
        titleBadgeClass: {
            type: String,
            default: 'ml-2',
        },
        // hiding members -- this only visually hides, data may still be there from API, won't hide for admins when modifying notifications for members
        // setting ANY of these to true will also visibly hide members in base-message-form also
        hideMembers: {  // visually hide all members, internal AND external? -- data may still be returned from API!
            type: Boolean,
            default: false,
        },
        hideExternalMembers: { // visually hide external TUs who are members? -- data may still be returned from API!
            type: Boolean,
            default: false,
        },
        hideInternalMembers: {  // visually hide internal TUs who are members? -- data may still be returned from API!
            type: Boolean,
            default: false,
        },
        showThreadAddress: {  // show the email address for the thread?
            type: Boolean,
            default: false,
        },
        noFiles: {  // if true, then do not show file / attachment uploads
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingList: false,
            // --- API DATA
            threadData: {},  // after splitting out membershipData and messageList, this will not hold many things, only things like title, email addresses, etc.
            // NEW - not keeping everything nested in threadData -- split out membershipData and messages so we can more easily update each one individually, since nested data reactivity is harder to manage
            membershipData: {},  // we split out membership related things after getting data from API, put it here - holds list of different types of members and perms for current TU
            messageList: [],  // we split out messages after getting from API, put it here
            currentTuIsNotify: false,  // boolean, is the current TU notify=True on this thread?  putting as top-level, we'll modify multiple ways (when getting thread_memberships, and/or when hitting subscribe/unsubscribe endpoint directly)
            loadingNotifyChange: false,  // loading toggle when changing currentTuIsNotify
            currentTuHasUnread: false, // will set this based on API data, and emit to parent also
            // --- END API DATA
            msgCreateModal: false,  // v-model for create modal 
            msgEditModal: false,  // v-model for edit modal
            editMsg: {},  // when we want to edit a comment, we'll set it here
            showNotifyMemberSelections: false,  // toggle for showing checkboxes to modify who is notified
            notifyMemberSelections: [],  // selected TUs when we are editing which participants/members are notified with checkboxes (options are a computed property)
            loadingNotifyEdit: false,  // loading indicator for when we're saving notify edits
            newMessageExpanderOpen: false,  // v-model for collapse/expander
            //
            accessDenied: false,  // if we get a 403, we show this in the message area (might be more common after tightening perms on Dec 3 2022)
        }
    },
    computed: {
        // has $currentUserInfo from mixin
        messageNameCapitalized: function() {
            return FormatCapitalizeHelper(this.messageName);
        },
        threadLastReadOnDateString: function() {  // return an ISO format date/time string
            // let defaultReadOn = '2000-01-01T00:00:00+00:00';  // y2k
            // UPDATE Dec 5 2021 - if you don't have a last_read_on date, then don't show a NEW badge... this will happen for tenant admins/superadmins who are reading thread they are not part of
            // just pass empty string -- BaseMessage component can handle it
            const defaultReadOn = '';  
            return this.membershipData.current_tu_membership?.read_status?.last_read_on || defaultReadOn;
        },
        notifyMemberOptions: function() {
            // return an array of objects to be used in checkbox component for which members to notify 
            // { full_name (text), id (value), and disabled (boolean)}
            // we will show all members, but disable any that do not have write access... if no write, then no notify
            // must go through list of thread_memberships, since that's where data is to do the filtering
            if (!this.membershipData || !this.membershipData.thread_memberships || !this.membershipData.thread_memberships.length ) return [];
            return this.membershipData.thread_memberships.map(tm => {
                return {
                    full_name: tm.tenant_user.full_name,
                    id: tm.tenant_user_id,
                    disabled: !tm.member_perms.can_write_thread,
                }
            });
        },
        notifyMemberName: function() {
            // return what 'notified members' are called for this thread type -- such as 'Followers' or 'Participants'
            // should be the capitalized, plural version
            let name = 'Followers';  // default
            if (this.threadType === 'comment') name = 'Followers';
            if (this.threadType === 'email') name = 'Participants';
            return name;
        },
        notifyMemberPopoverDescription: function() {
            // return the description of 'Followers' or 'Participants' shown in the popover, describing what these people are
            let standardPopover = `${this.notifyMemberName} will receive email notifications for any new ${this.messageName} sent from the app, or from an email reply.`;
            let popoverDesc = standardPopover;  // default
            if (this.threadType === 'comment') popoverDesc = standardPopover;
            if (this.threadType === 'email') popoverDesc = standardPopover;
            return popoverDesc;
        },
        showSelfNoficationToggle() {
            const currentTuPerms = this.membershipData?.current_tu_perms ?? {};
            return currentTuPerms.can_subscribe_self || currentTuPerms.can_unsubscribe_self;
        },
        notifyMemberModifyDescription: function() {
            // return the description used when user is add/remove/modify the notifyMembers
            let standardDescription = `${this.notifyMemberName} will receive notifications of any new ${this.messageName}.  Anyone not selected can still read all ${this.messageName}s if they log in, but will not receive notifications for new ${this.messageName}s.`;
            let retDesc = standardDescription;
            if (this.threadType === 'comment') retDesc = standardDescription;
            if (this.threadType === 'email') retDesc = standardDescription;
            return retDesc;
        },
        enableMessageCreate: function() {
            // check various things to see if 'create new message' button should be enabled (and reply buttons on each message)
            let tu_can_send = this.membershipData?.current_tu_perms?.can_write_thread || false;
            let threadOpen = (this.threadData.status && this.threadData.status === 'open') ? true : false;
            return tu_can_send && threadOpen;
        },
        viewerIsNotMember() {
            // return Boolean - true if the current viewer is NOT a member of the thread
            // this can happen for tenant admin/superadmin viewing thread they are not part of
            // we'll check the current_tu_membership object -- will be empty object if they aren't member, otherwise will be the ThreadMembership object
            const isMember = !!this.membershipData?.current_tu_membership?.id;
            return !isMember;
        },
    },
    filters: {
        capitalize: function(value) {
            return FormatCapitalizeHelper(value);
        },
        plural: function(value) {
            return value + 's';
        },
    },
    mounted: function() {
        this.getThread();
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        // has errorHandling mixin, so you can use this.errorToaster(error, content);
        setLocalData: function(apiThreadData) {
            // take apiThreadData as returned from API, slice and dice, split out messageList, membershipData, and set all local data
            let messages = apiThreadData.messages;  // messages may not be there if you didn't ask for them in request query params!
            if (messages && Array.isArray(messages) && messages.length) {
                this.setMessageList(messages);
            }
            let threadMemberships = apiThreadData.thread_memberships;  // thread_memberships may not be there if you didn't ask for them in request query params!
            const currentTuPerms = apiThreadData.current_tu_perms ?? {};  // added this to api response Dec 12 2022
            // what about setting defaults, in case we never ask for this??? just make sure you always get this on first request
            if (threadMemberships && Array.isArray(threadMemberships) && threadMemberships.length) {
                this.setMembershipData(threadMemberships, currentTuPerms);
            }
            // finally, set the local this.threadData
            let localThreadDataProperties = ['id', 'thread_title', 'inbound_email_address', 'outbound_email_address', 'status'];
            this.threadData = pick(apiThreadData, localThreadDataProperties);
            // nothing to return, this just sets local data
        },
        filterMembersToShow: function(membershipsToFilter) {
            // generic function to filter an array of threadMemberships based on this.hideMembers, hideExternalMembers, hideInternalMembers
            // takes an array, returns an array, so this can be chained
            if (!membershipsToFilter || !Array.isArray(membershipsToFilter)) return [];  // always return an array
            return membershipsToFilter.filter(tm => {
                let toShow = true;  // start with true for each, set to false along the way if we match any hide criteria
                if (this.hideMembers) toShow = false;
                if (this.hideExternalMembers && tm.tenant_user.is_external) toShow = false;  // if hiding external TUs, only let internal TUs pass
                if (this.hideInternalMembers && tm.tenant_user.is_internal) toShow = false;
                return toShow;
            })
        },
        setMembershipData: function(threadMemberships, currentTuPerms) {
            // takes thread_memberships from API, slices and dices, AND sets this.membershipData
            // ALSO will set this.currentTuHasUnread and emit event with it too
            let membershipData = {};  // start with an empty object, fill it up
            membershipData['thread_memberships'] = [...threadMemberships];  // its just a shallow copy....
            let currentTuId = this.$currentUserInfo.tenant_user_id;
            membershipData['members'] = threadMemberships.map(tm => tm.tenant_user);  // All TUs who are members
            membershipData['notify_members'] = threadMemberships.filter(tm => tm.notify).map(tm => tm.tenant_user);  // TUs who will be notified for new messages
            membershipData['notify_members_other'] = membershipData.notify_members.filter(tu => tu.id !== currentTuId);  // TUs who will be notified, other than current TU
            membershipData['no_notify_members'] = threadMemberships.filter(tm => !tm.notify).map(tm => tm.tenant_user);  // TUs who are part of thread, but will NOT be notified
            membershipData['notify_members_to_show'] = this.filterMembersToShow(threadMemberships).filter(tm => tm.notify).map(tm => tm.tenant_user);
            membershipData['read_members'] = threadMemberships.filter(tm => tm.can_read_thread).map(tm => tm.tenant_user);  // all TUs who can read
            membershipData['write_members'] = threadMemberships.filter(tm => tm.can_write_thread).map(tm => tm.tenant_user);  // all TUs who can write
            membershipData['full_members'] = threadMemberships.filter(tm => tm.role === 'full').map(tm => tm.tenant_user);  // all TUs with 'full' role
            membershipData['admin_members'] = threadMemberships.filter(tm => tm.role === 'admin').map(tm => tm.tenant_user);  // all TUs with 'admin' role
            // PULL OUT SPECIFIC PERMS FROM currentTuPerms we're now passing in - just to avoid neeing to change earlier stuff
            membershipData['current_tu_can_send'] = currentTuPerms.can_write_thread ?? false; // membershipData.write_members.map(fm => fm.id).includes(currentTuId);
            // NOT USED membershipData['current_tu_can_modify_memberships'] = membershipData.admin_members.map(am => am.id).includes(currentTuId);
            let currentTuMembership = threadMemberships.find(tm => tm.tenant_user_id === currentTuId);  // returns threadMembership object, or undefined
            membershipData['current_tu_membership'] = currentTuMembership ? currentTuMembership : {};  // set it to be empty object if not found - will happen if a tenant admin/superadmin is viewing thread they are not part of
            this.currentTuHasUnread = membershipData.current_tu_membership?.read_status?.has_unread;
            this.$emit('unread-status', this.currentTuHasUnread);  // maybe emit some thread info too?  so up the chain its easy to connect a message thread ID to unread status
            membershipData['current_tu_perms'] = {...currentTuPerms};  // currentTuMembership && currentTuMembership.member_perms ? currentTuMembership.member_perms : {}; 
            // finally, set the local data values
            if (currentTuMembership && has(currentTuMembership, 'notify')) {
                this.currentTuIsNotify = currentTuMembership.notify;  // only set this if we just fetched data and we know we have currentTuMembership data, or else don't modify it
            }
            this.membershipData = Object.assign({}, membershipData);  
        },
        setMessageList: function(messages) {
            // takes messages from API, slices and dices (if needed), AND sets this.messageList
            this.messageList = messages;
        },
        getThread: function(skipMessages, skipMemberships) {
            // if you set skipMessages or skipMemberships to true, we won't get those... but don't do that on first request
            if (!this.threadId) return;  // have to skip this if we don't have threadId... in case we never created one somewhere we're using this component
            let showLoading = !skipMessages;  // only show loading indicator if we're reloading messages... not if we're only reloading memberships
            if (showLoading) this.loadingList = true;
            this.accessDenied = false; // reset this
            let axiosConfig = {
                params: {  // set booleans for messages and thread_memberships
                    messages: skipMessages ? false : true,  // we must check for 'true' as a string on backend...
                    thread_memberships: skipMemberships ? false : true,  // must check for 'true' as a string on backend...
                }
            };
            return suppliosApi.messageThread.retrieveDynamic(this.threadId, axiosConfig)
                .then(response => {
                    this.setLocalData(response.data);  // this slices and dices, then sets everything, can tolerate messages or thread_memberships not being there (except for first request)
                    return true;
                })
                .catch(err => {
                    if (err.response.status === 403) {
                        // got permission denied?  show something about that rather than just showing error toast
                        this.accessDenied = true;
                        return false;
                    }
                    this.apiErrorHandler(err);
                    return false;
                })
                .then(status => {
                    this.loadingList = false;
                    return status;
                })
        },
        refreshMessages: function() {
            this.getThread(false, false);  // just still fetch everything for now... no advantage yet for not refreshing everything
        },
        refreshMemberships: function() {
            // call getThread with skipMessages... just get the other stuff
            this.getThread(true, false);
        },
        showMessageModal: function() {
            this.msgCreateModal = true;
        },
        handleMsgCreated: function(newMsgObj) {
            // this is called once message create form emits 'created' event -- created message should be in the payload if you want it
            this.msgCreateModal = false;
            //this.refreshMessages();  // no need for this... since we'll just push new data locally
            this.refreshMemberships();  // need to refresh new memberships in case new message added someone to notify=True
            this.messageList.unshift(newMsgObj);  // don't reload the list... just add it!  
        },
        msgCreateModalHidden: function() {
            // reset some things
        },
        handleEditRequest: function(editObj) {
            this.editMsg = Object.assign({}, cloneDeep(editObj));
            this.$nextTick(() => {
                this.msgEditModal = true;
            });
        },
        handleReplyRequest: function(msgObj) {
            // handles 'reply' event from BaseMessage -- msgObj is the message user clicked on when clicking reply... if you will do nested comments/messages
            console.log(msgObj);
            this.showMessageModal();
        },
        handleMsgUpdated: function() {
            // this is called once message edit form emits 'updated' event -- updated message should be in the payload if you want it
            this.msgEditModal = false;
            this.getThread();  // refresh everything... messages and memberships -- messages because we just edited, and memberships because maybe we tagged someone new?
        },
        msgEditModalHidden: function() {
            this.editMsg = {};
        },
        startNotifyEdit: function() {
            // call this when we want to show checkboxes to modify which TUs are notified
            // should only be for admins, people who can modify notify members
            // first, figure out which checkboxes should already be selected, should be array of TU ids -- do this before showing checkboxes
            this.notifyMemberSelections = this.membershipData.notify_members ? this.membershipData.notify_members.map(nm => nm.id) : [];
            // now show checkboxes
            this.showNotifyMemberSelections = true;
        },
        saveNotifyEdit: function() {
            // make API call to save which TUs/members are notified
            // get TU ids from this.notifyMemberSelections
            //let notifyTuIds = this.notifyMemberSelections;
            //console.log(notifyTuIds);
            this.loadingNotifyEdit = true;
            let data = {
                notify_tu_ids: this.notifyMemberSelections,
            };
            return suppliosApi.messageThread.modifyNotifyMembers(this.threadId, data)
                .then(response => {
                    // pull out updated list of thread_memberships, use our method for setting all local membershipData
                    let threadMemberships = response.data.thread_memberships;
                    const currentTuPerms = response.data.current_tu_perms;  // added to api response Dec 12 2022
                    if (threadMemberships && threadMemberships.length) {
                        this.setMembershipData(threadMemberships, currentTuPerms);
                    }
                    this.showNotifyMemberSelections = false;
                    return true;
                })
                .catch(error => {
                    this.apiErrorHandler(error)
                    return false;
                })
                .then(success => {
                    this.loadingNotifyEdit = false;
                    return success;
                })
        },
        cancelNotifyEdit: function() {
            // cancel / do not save edits to who is notified
            this.showNotifyMemberSelections = false;
        },
        openNewMessage: function() {
            // open the new-message-expander
            this.newMessageExpanderOpen = true;
        },
        cancelNewMessage: function() {
            // close the new-message-expander
            this.newMessageExpanderOpen = false;
        },
        changeCurrentTuNotify: function(newVal) {
            this.loadingNotifyChange = true;
            console.log('from changeCurrentTuNotify, newVal:');
            console.log(newVal);
            return suppliosApi.messageThread.modifyOwnSubscription(this.threadId, newVal)
                .then(response => {
                    // we will respond with object with one key: notify (boolean for confirmed new setting)
                    this.currentTuIsNotify = response.data.notify;
                    // then we must update thread_memberships so list of participants/followers is correct
                    this.refreshMemberships();
                    return true;
                })
                .catch(error => {
                    // various error codes possible, we'll pass back a string in error.response.data with message to display
                    this.apiErrorHandler(error);
                    return false;
                })
                .then(success => {
                    this.loadingNotifyChange = false;
                    return success;
                })
        },
    }
}
</script>

<style lang="scss">

.create-message-button {
    opacity: 1;

    &.disabled,
    &:disabled {
        opacity: 1;
    }
}
.message-thread-header-separator {
    height: 2px;
    background-color: var(--gray-200);
    width: 100%;
}

.message-list-item {
    margin-top: 1.5rem;
}

.message-list-item:first-child {
    margin-top: 0;
}

.message-list-move {
    transition: all 0.5s;
}
.message-list-enter-active {
    transition: all 0.5s;
}
.message-list-enter, 
.message-list-leave-to {
    opacity: 0;
    /* transform: translateY(30px); */
    transform: scale(0.3, 0.3);
}

</style>